// ======================================================================
// FILE:
// src/modules/finance/services/financeLookupService.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Finance Lookups
//
// PURPOSE:
// Frontend API client for Finance lookup data used by Finance screens.
//
// RESPONSIBILITIES
// - Search Finance Companies
// - Search Financial Years
// - Search Financial Periods
// - Search Chart of Accounts
// - Search Cost Centers
// - Search Currencies
//
// NOTES
// - Uses axiosClient only
// - No React code
// - No UI logic
// - No Toast
// - No Navigation
// - No State Management
// - Read-only API client
//
// AUTHOR:
// DizyPro ERP
// ======================================================================

import axiosClient from "../../../api/axiosClient";

// ======================================================================
// API BASE URL
// ======================================================================

const BASE_URL =
    "/api/finance/lookups";

// ======================================================================
// SEARCH COMPANIES
// ======================================================================

async function searchCompanies(
    search = "",
    limit = 20
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/companies`,
            {
                params: {
                    search,
                    limit,
                },
            }
        );

    return (
        response.data.data || []
    );
}

// ======================================================================
// SEARCH FINANCIAL YEARS
// ======================================================================

async function searchFinancialYears(
    search = "",
    limit = 20
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/financial-years`,
            {
                params: {
                    search,
                    limit,
                },
            }
        );

    return (
        response.data.data || []
    );
}

// ======================================================================
// SEARCH FINANCIAL PERIODS
// ======================================================================

async function searchFinancialPeriods(
    financialYearId = null,
    search = "",
    limit = 20
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/financial-periods`,
            {
                params: {
                    financial_year_id:
                        financialYearId,
                    search,
                    limit,
                },
            }
        );

    return (
        response.data.data || []
    );
}

// ======================================================================
// SEARCH CHART OF ACCOUNTS
// ======================================================================

async function searchChartOfAccounts(
    companyId = null,
    search = "",
    accountType = null,
    limit = 20
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/chart-of-accounts`,
            {
                params: {
                    company_id:
                        companyId,
                    account_type:
                        accountType,
                    search,
                    limit,
                },
            }
        );

    const rows =
        response.data.data || [];

    return rows.map(
        (row) => ({
            ...row,

            value:
                row.id ??
                row.coa_id ??
                row.chart_of_account_id,

            label:
                row.account_name
                    ? (
                        row.account_code
                            ? `${row.account_code} - ${row.account_name}`
                            : row.account_name
                    )
                    : (
                        row.account_code ||
                        row.name ||
                        String(
                            row.id ??
                            row.coa_id ??
                            ""
                        )
                    ),
        })
    );
}

// ======================================================================
// SEARCH COST CENTERS
// ======================================================================

async function searchCostCenters(
    companyId = null,
    search = "",
    limit = 20
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/cost-centers`,
            {
                params: {
                    company_id:
                        companyId,
                    search,
                    limit,
                },
            }
        );

    const rows =
        response.data.data || [];

    return rows.map(
        (row) => ({
            ...row,

            value:
                row.id ??
                row.cost_center_id,

            label:
                row.cost_center_name
                    ? (
                        row.cost_center_code
                            ? `${row.cost_center_code} - ${row.cost_center_name}`
                            : row.cost_center_name
                    )
                    : (
                        row.cost_center_code ||
                        row.name ||
                        String(
                            row.id ??
                            row.cost_center_id ??
                            ""
                        )
                    ),
        })
    );
}

// ======================================================================
// SEARCH CURRENCIES
// ======================================================================

async function searchCurrencies(
    search = "",
    limit = 20
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/currencies`,
            {
                params: {
                    search,
                    limit,
                },
            }
        );

    const rows =
        response.data.data || [];

    return rows.map(
        (row) => ({
            ...row,

            value:
                row.id ??
                row.currency_id,

            label:
                row.currency_name
                    ? (
                        row.currency_code
                            ? `${row.currency_code} - ${row.currency_name}`
                            : row.currency_name
                    )
                    : (
                        row.currency_code ||
                        row.code ||
                        row.name ||
                        String(
                            row.id ??
                            row.currency_id ??
                            ""
                        )
                    ),
        })
    );
}

// ======================================================================
// SEARCH PAYMENT ACCOUNTS
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * SEARCH PAYMENT ACCOUNTS
 * ----------------------------------------------------------------------
 *
 * Backend
 * GET /api/finance/lookups/payment-accounts
 *
 * Parameters
 * - companyId
 * - search
 * - limit
 *
 */

async function searchPaymentAccounts(
    companyId = null,
    search = "",
    limit = 20
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/payment-accounts`,
            {
                params: {
                    company_id:
                        companyId,
                    search,
                    limit,
                },
            }
        );

    return (
        response.data.data || []
    );
}
// ======================================================================
// SEARCH VENDORS
// ======================================================================

async function searchVendors(
    search = ""
) {

    const response =
        await axiosClient.get(
            "/api/ppe/masters/business-partner/vendors/lookup",
            {
                params: {
                    search,
                },
            }
        );

    return (
        response.data?.data?.items ||
        []
    );

}
// ======================================================================
// SEARCH CUSTOMERS
// ======================================================================

async function searchCustomers(
    search = ""
) {

    const response =
        await axiosClient.get(
            "/api/ppe/masters/business-partner/customers/lookup",
            {
                params: {
                    search,
                },
            }
        );

    return (
        response.data?.data?.items ||
        []
    );

}
// ======================================================================
// EXPORT
// ======================================================================

const financeLookupService = {

    searchCompanies,

    searchFinancialYears,

    searchFinancialPeriods,

    searchChartOfAccounts,

    searchCostCenters,

    searchCurrencies,
    searchPaymentAccounts,
    searchVendors,

    searchCustomers,

};

export default financeLookupService;